import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Record } from 'immutable';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';

// Actions
import * as GoalActions from 'Shared/actions/Goals.actions';
import * as MeetingItemsActions from 'Shared/actions/MeetingItems.actions';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';

// Selctors
import { getGoalsByUserIdAndComplete } from 'Shared/selectors/Goals.selectors';

// Components
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import WpSelect from 'Wpds/components/select/WpSelectV2';
import GoalIcon from 'Shared/components/Goals/GoalIcon';

// Utils
import { trackOpenedModal } from 'Shared/tracking/tracking';

// Constants
import TrackingConstants from 'Shared/constants/Tracking.constants';

// Styles
import './AddToGoalModal.scss';

type Props = {
  meetingItem: Record<MeetingItem>;
  isOpen: boolean;
  toggleModal: () => void;
};

const AddToGoalModal = ({ meetingItem, isOpen = false, toggleModal }: Props) => {
  const dispatch = useDispatch();
  const [selectedGoalSlug, setSelectedGoalSlug] = useState(null);
  const [isSaving, toggleIsSaving] = useToggle(false);

  const itemUuid = MeetingItemHelpers.getUuid(meetingItem);
  const goalSlug = MeetingItemHelpers.getGoalSlug(meetingItem);
  const { id: userId } = useCurrentUser();
  const goals = useSelector(state => getGoalsByUserIdAndComplete(state, { complete: false, userId }))
    .valueSeq()
    .toJS();

  const formattedOptions = goals.map(goal => {
    return { ...goal, value: goal.slug, label: goal.name };
  });

  useEffect(() => {
    setSelectedGoalSlug(goalSlug);
  }, [goalSlug]);

  useEffect(() => {
    if (isOpen) {
      trackOpenedModal(TrackingConstants.MODAL_TYPES.ADD_TO_GOAL);
    }
  }, [isOpen]);

  useEffect(() => {
    dispatch(GoalActions.fetchGoals({ context: 'team', completed: false }));
  }, []);

  const handleSelectGoal = selectedGoal => {
    setSelectedGoalSlug(selectedGoal.value);
  };

  const handleAddToGoalClick = () => {
    toggleIsSaving();
    dispatch(MeetingItemsActions.addToGoal(itemUuid, selectedGoalSlug)).then(() => {
      toggleIsSaving();
      toggleModal();
    });
  };

  const baseClassName = 'AddToGoalModal';

  const customGoalOption = ({ option }) => {
    return (
      <div className={`${baseClassName}__select-option`}>
        <GoalIcon icon={option.icon} status={option.status} size="sm" />
        <span className={`${baseClassName}__select-option__label`}>{option.label}</span>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <WpModal isOpen={isOpen} size="sm" onToggle={toggleModal}>
      <WpModalHeader>Add to goal</WpModalHeader>
      <WpModalBody>
        <div>
          <p>Choose the goal you would like to add this item to.</p>
          <WpSelect
            id="goal-select-input"
            label="Goal"
            labelHidden
            isSearchable
            options={formattedOptions}
            searchPlaceholder="Search Goals"
            value={selectedGoalSlug}
            onChange={handleSelectGoal}
            placeholder="Select goal"
            components={{
              option: customGoalOption,
            }}
          />
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={toggleModal} disabled={isSaving}>
            Cancel
          </WpButton>
          <WpButton onClick={handleAddToGoalClick} loading={isSaving} disabled={goalSlug === selectedGoalSlug}>
            Add to Goal
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default AddToGoalModal;
